// page-state.js — page-local mutable state objects.
//
// Each per-page state lives here as a named export. Both
// src/bootstrap.js (during the migration) and the page
// modules import directly — no bridge round-trip needed.
//
// Why not put each one in its own pages/<name>.js module export?
// Two reasons:
//   1. The legacy fetch/render helpers that haven't moved yet
//      depend on these objects. Putting them in pages/*.js would
//      create a circular import (legacy needs page-state; page
//      module already imports from legacy via the bridge).
//   2. Centralizing makes "what state does the dashboard hold?"
//      easy to answer — one grep in this file.
//
// When the AC_LEGACY bridge goes away (last few entries clear out
// of bootstrap.js), these can move into their per-page module as
// private state if the page is the only consumer. Until then,
// central + named is simpler.

import { state } from "./state.js";

// Team page state. Cached members + invite info; lastFetch gates
// the 5s re-fetch in renderTeamPage.
export const teamState = {
  info: null,
  members: [],
  inviteCode: null,
  loading: false,
  lastFetch: 0,
  editing: false,
  settings: null, // { version, values, updated_at, updated_by }
  settingsDirty: false, // unsaved user edits
};

// Handoffs / Messages page state. Thread list + expansion +
// compose drawer. Used by both src/pages/handoffs.js and legacy
// renderers that still own the compose/reply/expand flows.
export const handoffsState = {
  threads: [],
  expanded: null, // context_id of expanded thread, if any
  expandedRows: [],
  composing: false, // composing a brand-new handoff
  replyingTo: null, // task_id of parent we're replying to
  lastFetchAt: 0,
  // returnPage records where to send the user when they hit "Back"
  // inside a thread. Set by openThreadDrawer when click-through
  // originates from Coordination. Empty = legacy behavior (back
  // returns to the threads list on this page).
  returnPage: "",
};

// Policy page state — currently-loaded ruleset + load status.
export const policyState = { data: null, loading: false, error: null };

// Policy list filter — text + decision-class chip + expanded rows.
export const policyListFilter = {
  text: "", // case-insensitive substring search over name+reason+path
  decision: "all", // 'all' | 'forbid' | 'require_confirm' | 'audit' | 'default'
  expanded: new Set(), // rule names whose row is expanded
};

// Priority page state — currently-loaded ruleset + load status.
export const priorityState = {
  data: null,
  loading: false,
  error: null,
};

// Priority list filter — same shape as policyListFilter but with
// priority-specific decision chip values.
export const priorityListFilter = {
  text: "",
  decision: "all", // 'all' | 'preempt' | 'no-op' | 'default'
  expanded: new Set(),
};

// Spend page state — current window + per-repo filter + cached data.
// window is one of '24h' / '7d' / '30d'. The page has a single view, the
// per-chat rollup ("which chats are driving cost?"), cached in dataByChat.
export const spendState = {
  dataByChat: null,
  loading: false,
  error: null,
  // Default to 30d: the value/ROI framing is cumulative, and a 24h
  // window often has zero prevented-collisions, which made AC's
  // contribution look empty. 30d tells the real story on first load.
  window: "720h",
  repoFilter: "",
  // filterText is the spend search query, sent to the server as ?q= so it
  // matches across ALL rows (the table is server-paginated), not just the
  // visible page. Resets page to 0 on change.
  filterText: "",
  // value holds the /v1/value triangulation (collisions + edits + real
  // spend → $ saved) for the "Value delivered" band at the top of the
  // Spend page. null until first fetch / on error.
  value: null,
  // planSelections maps a provider key ("anthropic"/"openai"/"gemini")
  // to the user's chosen plan for the "what you pay" comparison:
  //   { tier: "<tier id>" | "custom", accounts: <int>, customUSD: <number> }
  // This is a pure DISPLAY overlay (public list prices, no server math).
  // It persists at the ACCOUNT (tenant) level server-side — team_settings
  // .plan_selections (migration 059), carried in /v1/teams/settings — with
  // localStorage as a fast local cache. Loaded by loadPlanSelections()
  // (server-first hydrate + migrate-from-local) on first render; saved by
  // savePlanSelections() (local cache + best-effort server PUT). A provider
  // only gets a picker when it has spend rows in the current window
  // (ledger-driven).
  planSelections: {},
  // _planServerChecked flips true once loadPlanSelections has ATTEMPTED the
  // server hydrate (success or failure). The first-visit auto-open of the
  // plan modal waits on this so a user who set their plan on another browser
  // (server has it, local cache empty) is NOT re-prompted before the server
  // value lands. Undefined/false = server not yet consulted.
  _planServerChecked: false,
  // Server-side pagination of the spend table. page is 0-based; the
  // fetcher sends limit=pageSize & offset=page*pageSize, and the server
  // returns total_rows so the pager knows how many pages exist. Reset to
  // 0 on any window-change or view-mode toggle (the row set changes).
  page: 0,
  pageSize: 25,
  // Set of incident reference ids (event_id / lease_id) whose Recent collision
  // row is expanded to show the full writeup. Toggled by clicking the row.
  expandedIncidents: new Set(),
  // showAllIncidents: when true the Protection "Recent" band renders every
  // incident the payload carries (not just the inline cap), so the user can
  // see all of them WITHOUT navigating to the Events page. Toggled by the
  // "Show all" control in protectionExamplesHTML's footer.
  showAllIncidents: false,
  // Flagged-event feed (the searchable/expandable list below the band, fed from
  // state.events filtered by isFlaggedEvent). flaggedFilter is the search query
  // (client-side, matches type/file/agent/purpose); flaggedPage is the 0-based
  // page. Reset to page 0 on a new query.
  flaggedFilter: "",
  flaggedPage: 0,
  // flaggedOnly: { types, label } when a Protection number narrowed the list.
  flaggedOnly: null,
};

// coordinationState — cloud-mode Coordination feed (decisions + messages).
// Server-paginated per source: each fetcher sends limit=pageSize &
// offset=page*pageSize & since=window & q=search and the server returns
// total_rows so the pager knows the page count. Decisions and messages
// page independently (decPage/msgPage) since they're separate endpoints.
export const coordinationState = {
  decisions: [],
  messages: [],
  decTotal: 0,
  msgTotal: 0,
  // False until the first fetch of that source finishes, so the feed shows
  // a pending count instead of a false "(0)" while the request is in flight.
  decLoaded: false,
  msgLoaded: false,
  decPage: 0,
  msgPage: 0,
  pageSize: 50,
  // Time window sent as ?since=. "" = all time. Default 7d (168h).
  window: "168h",
  // Search query. Plaintext fields (agent/machine/subsystem/thread) go to
  // the server as ?q= so paging is correct; encrypted subject/summary are
  // additionally filtered client-side over loaded rows.
  q: "",
  loading: false,
  error: null,
};

// collabDrillState — the Coordination page's list behind one Analytics
// Collaboration number (collab-drill.js). drill is {kind, days} or null.
// loadedKey is the kind|days|page last fetched, so ticks don't refetch.
export const collabDrillState = {
  drill: null,
  items: [],
  hasText: false,
  total: 0,
  page: 0,
  pageSize: 50,
  loadedKey: "",
  loading: false,
  error: null,
};

// PLAN_CATALOG — public monthly subscription list prices per provider,
// used only to render the "what you pay" side of the Spend hero. These
// are display reference values, not billed amounts. Each provider also
// supports a "token-based" choice (tier id "tokens") meaning the user
// pays per-token via an API key — in that case "what you pay" IS the
// metered API-equivalent cost (no flat plan), so savings vs. a plan is
// not the framing; we just show the cost. `logo` is an inline SVG path
// d-attribute-free mark kept simple + theme-colored via currentColor.
export const PLAN_CATALOG = {
  anthropic: {
    label: "Claude",
    logo: "anthropic",
    tiers: [
      { id: "pro", label: "Pro", usd: 20 },
      { id: "max5", label: "Max 5×", usd: 100 },
      { id: "max20", label: "Max 20×", usd: 200 },
    ],
  },
  openai: {
    label: "ChatGPT",
    logo: "openai",
    tiers: [
      { id: "plus", label: "Plus", usd: 20 },
      { id: "pro", label: "Pro", usd: 200 },
    ],
  },
  gemini: {
    label: "Gemini",
    logo: "gemini",
    tiers: [
      { id: "aipro", label: "AI Pro", usd: 20 },
      { id: "aiultra", label: "AI Ultra", usd: 250 },
    ],
  },
};

// PROVIDER_LOGO returns a small inline SVG mark for a provider, sized
// 16px and colored via currentColor so it adapts to light/dark. Kept
// deliberately simple (geometric marks, not exact brand glyphs) to
// avoid shipping trademarked assets.
export function PROVIDER_LOGO(provider) {
  const wrap = (inner) =>
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">${inner}</svg>`;
  switch (provider) {
    case "anthropic":
      // simple "A" chevron mark
      return wrap('<path d="M5 19 12 5l7 14"/><path d="M8.5 14h7"/>');
    case "openai":
      // hexafoil-ish ring
      return wrap('<circle cx="12" cy="12" r="8"/><path d="M12 4v16M4 12h16"/>');
    case "gemini":
      // four-point spark
      return wrap('<path d="M12 3c.6 4.5 4.5 8.4 9 9-4.5.6-8.4 4.5-9 9-.6-4.5-4.5-8.4-9-9 4.5-.6 8.4-4.5 9-9z"/>');
    default:
      return wrap('<circle cx="12" cy="12" r="9"/>');
  }
}

const PLAN_STORAGE_KEY = "ac.spend.planSelections";

// The plan/pricing selection is an ACCOUNT-LEVEL (tenant) setting: it lives
// server-side on the team_settings tenant-default row (bu_id='') under the
// plan_selections column (migration 059), carried in the same object as the
// rest of /v1/teams/settings. localStorage is only a fast local cache so the
// hero paints instantly and offline/solo mode still works — the server is the
// source of truth, so a new login / new browser / cleared cookies no longer
// re-prompts. See loadPlanSelections (server-first hydrate + migrate-from-
// local) and savePlanSelections (local cache + best-effort server PUT).

// hasStoredPlan reports whether an object is a real (non-empty) plan
// selection — any provider key or the synthetic __total. Shared by the
// hydrate/migrate logic so "did the server/local actually have a plan?" is
// asked one way.
function hasStoredPlan(sel) {
  if (!sel || typeof sel !== "object") return false;
  if (typeof sel.__total === "number") return true;
  return Object.keys(sel).some((k) => k !== "__total" && sel[k] && sel[k].tier);
}

// readLocalPlan pulls the localStorage cache (best-effort). Returns {} on any
// failure (private mode, malformed).
function readLocalPlan() {
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch (_e) {
    /* storage unavailable / malformed */
  }
  return {};
}

// writeLocalPlan mirrors the current selections into the localStorage cache.
function writeLocalPlan(sel) {
  try {
    localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(sel || {}));
  } catch (_e) {
    /* storage unavailable (private mode); selections stay in-memory */
  }
}

// putPlanToServer writes the account-level plan_selections to the tenant
// settings row via the same PUT the Settings page uses. Best-effort: the
// caller decides whether to surface a failure. Imported lazily so page-state
// stays free of a hard api.js dependency at module-eval time (avoids any
// import-order surprise) and so daemon/solo mode — where the endpoint is a
// read-only stub — degrades to a quiet no-op instead of throwing.
async function putPlanToServer(sel) {
  // A local daemon that isn't on a team has no account to save to; the
  // browser cache above is where the plan lives there (one computer, one
  // browser), so there is nothing to send.
  if (state.teamInfo && state.teamInfo.solo) return;
  const { apiWithMessage } = await import("./api.js");
  // Account-level: always the tenant-default row (no bu_id). The plan/pricing
  // choice is account-wide, not per-workspace.
  await apiWithMessage("/v1/teams/settings", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan_selections: sel || {} }),
  });
}

// loadPlanSelections hydrates spendState.planSelections. Server-first so the
// choice follows the ACCOUNT, not the browser:
//   1. Hydrate synchronously from the localStorage cache so the first paint
//      isn't blocked on a network round-trip.
//   2. Fetch the server settings. If the server has a plan, it WINS — hydrate
//      from it and refresh the local cache (this is what makes a new login /
//      new browser show the saved plan instead of re-prompting).
//   3. If the server has nothing but local does, MIGRATE the local value up to
//      the server so it persists going forward.
// Best-effort throughout: offline / solo / daemon mode just keeps the local
// value. `onServerHydrated` (optional) is called after the async server step
// resolves so the caller can re-render if the server value differed.
export function loadPlanSelections(onServerHydrated) {
  // Step 1 — synchronous local hydrate (fast paint).
  const local = readLocalPlan();
  spendState.planSelections = local;

  // Step 2/3 — reconcile with the server asynchronously.
  (async () => {
    let data;
    try {
      const { api } = await import("./api.js");
      data = await api("/v1/teams/settings");
    } catch (_e) {
      // Offline / not-authed / solo stub error — keep the local value. Still
      // mark the server as checked + let the caller re-render so the
      // first-visit modal gate stops waiting (it falls back to the local
      // value, which is the right behavior when the server is unreachable).
      spendState._planServerChecked = true;
      if (typeof onServerHydrated === "function") onServerHydrated();
      return;
    }
    spendState._planServerChecked = true;
    const server =
      data && data.plan_selections && typeof data.plan_selections === "object"
        ? data.plan_selections
        : {};
    if (hasStoredPlan(server)) {
      // Server wins — hydrate + refresh the local cache.
      spendState.planSelections = server;
      writeLocalPlan(server);
      if (typeof onServerHydrated === "function") onServerHydrated();
      return;
    }
    // Server empty. If we have a local value, push it up so it persists.
    if (hasStoredPlan(local)) {
      try {
        await putPlanToServer(local);
      } catch (_e) {
        /* best-effort migrate; local cache still carries it */
      }
    }
    // Let the caller re-render — the gate was released (server checked).
    if (typeof onServerHydrated === "function") onServerHydrated();
  })();
}

// savePlanSelections persists the current selections to BOTH the localStorage
// cache (instant, offline-safe) AND the account-level server row. The
// returned promise rejects when the account save fails, so the caller can
// tell the user the plan is kept on this browser only.
export function savePlanSelections() {
  const sel = spendState.planSelections || {};
  writeLocalPlan(sel);
  return putPlanToServer(sel);
}

// planMonthlyUSD sums the selected FLAT-PLAN selections into a monthly
// dollar total. Only providers present in `meteredProviders` contribute.
// Token-based selections (tier "tokens") contribute 0 here — for those
// the user pays the metered cost, which the hero handles separately via
// isTokenBased(). Returns 0 when nothing flat is selected.
export function planMonthlyUSD(meteredProviders) {
  const sel = spendState.planSelections || {};
  // A custom "total $/mo" (set via the modal's "enter a total" path)
  // supersedes per-provider math.
  if (typeof sel.__total === "number") return Math.max(0, sel.__total);
  let total = 0;
  // Sum over ALL saved provider selections, not just providers with spend in
  // the current window. A flat subscription is a fixed monthly cost — it
  // doesn't drop to $0 just because the selected window has no usage. (The
  // old version iterated only `meteredProviders`, so the plan figure showed
  // $0/mo in an empty window even though the user pays a flat fee.)
  const providers = Object.keys(sel).filter((k) => k !== "__total");
  for (const provider of providers) {
    const choice = sel[provider];
    if (!choice) continue;
    if (choice.tier === "tokens") continue; // metered, not flat
    if (choice.tier === "custom") {
      total += Math.max(0, Number(choice.customUSD) || 0);
      continue;
    }
    const accounts = Math.max(1, parseInt(choice.accounts, 10) || 1);
    const cat = PLAN_CATALOG[provider];
    if (!cat) continue;
    const tier = cat.tiers.find((t) => t.id === choice.tier);
    if (tier) total += tier.usd * accounts;
  }
  return total;
}

// hasAnyPlanSelection reports whether the user has made ANY plan choice —
// i.e. the plan modal has been answered. Drives whether we still need to
// prompt with "Set plan."
//
// It does NOT gate on `meteredProviders`. The old version only counted a
// selection for a provider that ALSO appears in meteredProviders, so when
// the current window has 0 spend (no metered providers), a user who HAD
// set a plan was shown "Set plan" again — the plan appeared to vanish in
// an empty window. A plan, once chosen, is a persisted preference; it must
// not disappear just because the selected time window happens to have no
// usage. So: any saved per-provider selection (or a custom __total) counts.
export function hasAnyPlanSelection(meteredProviders) {
  const sel = spendState.planSelections || {};
  if (typeof sel.__total === "number") return true;
  // Any saved provider selection with a tier counts, regardless of whether
  // that provider has spend in the current window.
  return Object.keys(sel).some(
    (k) => k !== "__total" && sel[k] && sel[k].tier,
  );
}

// allTokenBased reports whether every metered provider the user
// configured is token-based (pay-per-token via API key). When true the
// "savings vs. a flat plan" framing doesn't apply — they pay the
// metered cost — so the hero shows cost rather than a savings gap.
export function allTokenBased(meteredProviders) {
  const sel = spendState.planSelections || {};
  const configured = (meteredProviders || []).filter((p) => sel[p] && sel[p].tier);
  if (configured.length === 0) return false;
  return configured.every((p) => sel[p].tier === "tokens");
}

// Decisions (Coordination) page state — filters + cached list.
// source filters by generated vs user-edited; subsystem filters
// to one subsystem; sinceSeconds filters to a time window;
// agent does substring match on agent_id.
export const decisionsState = {
  list: [],
  loading: false,
  error: null,
  filter: "",
  source: "all", // 'all' | 'user' | 'auto'
  subsystem: "", // empty = all subsystems
  sinceSeconds: 0, // 0 = no time cutoff
  agent: "", // substring match on agent_id
};

// Agents page state — registered AI session list + filter.
export const agentsState = {
  list: [],
  loading: false,
  error: null,
  filter: "",
  states: {},
  leasesByAgent: {},
  worktreesByAgent: {},
  spendByAgent: {},
  machineByAgent: {},
  fetchedAt: 0,
  showEnded: false,
  openFiles: new Set(),
  // Remove button: ids with a DELETE in flight, and the error a failed
  // remove left on its row.
  removing: new Set(),
  removeErrors: {},
  // only: set when a number elsewhere (Overview "Active chats") opened this
  // page for a given set of chats: { ids: Set, total, label }.
  only: null,
};

// Worktrees page state — cached list + active count + disk usage.
// list === null means unloaded; [] means loaded but empty.
export const worktreesState = {
  list: null,
  activeCount: 0,
  totalDiskMB: 0,
  scope: "active", // 'active' | 'all'
  filter: "",
  loading: false,
  error: null,
};
